/**
 * ELITE BATCH 22 PART 7 — Workflows 76-88 (Attentive)
 * 13 SMS-first marketing workflows for the Attentive platform.
 * Each workflow: 8 nodes, proper edges, detailed summary.
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 76-88 ============

export const ELITE_BATCH22_PART7: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ────────────────────────────────────────────────────────────────
  // 76. Birthday SMS Offer | Attentive | Pattern H
  // ────────────────────────────────────────────────────────────────
  birthday_sms_offer: {
    nodes: [
      trigger("Attentive: Birthday event fires", "schedule", {
        source: "attentive",
        event: "subscriber.birthday",
        schedule:
          "Daily at 8 AM — checks for subscribers whose birthday is today",
        howItStarts:
          "Runs daily and identifies Attentive subscribers whose birthday matches today's date, triggering a personalized SMS celebration offer.",
        setup:
          "Connect Attentive subscriber list with birthday date field. Schedule runs daily at 8 AM in subscriber's timezone.",
        inputFields: [],
      }),
      n("2", "hitApi", "Attentive: Fetch subscriber loyalty profile", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "attributes.phone",
              customName: "subscriber_phone",
              type: "string",
            },
            {
              name: "attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "attributes.loyalty_tier",
              customName: "loyalty_tier",
              type: "string",
            },
            {
              name: "attributes.total_purchases",
              customName: "total_purchases",
              type: "number",
            },
            {
              name: "attributes.ltv",
              customName: "customer_ltv",
              type: "number",
            },
          ],
        },
      }),
      n("3", "condition", "Check if VIP tier subscriber", 520, {
        conditionDetails: {
          rules: [{ field: "loyalty_tier", operator: "equals", value: "VIP" }],
        },
      }),
      smsNode(
        "4",
        "Attentive: Send VIP birthday SMS with 30% off",
        760,
        "{subscriber_phone}",
        "Happy Birthday, {first_name}! 🎂 As a VIP member, enjoy an exclusive 30% off everything today. Use code VIPBDAY30 at checkout: {store_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "4b",
        "Attentive: Send standard birthday SMS with 20% off",
        760,
        "{subscriber_phone}",
        "Happy Birthday, {first_name}! 🎉 Celebrate with 20% off your next order. Use code BDAY20 — valid for 7 days: {store_link}. Reply STOP to opt out.",
        480,
      ),
      n("5", "delayAction", "Wait 5 days for redemption window", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "Check if birthday offer was redeemed", 1240, {
        conditionDetails: {
          rules: [
            { field: "offer_redeemed", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "7",
        "Attentive: Send redemption reminder SMS",
        1480,
        "{subscriber_phone}",
        "Hey {first_name}, your birthday treat is about to expire! Don't miss your {discount_amount} off — use it before midnight: {store_link}. Reply STOP to opt out.",
      ),
      sheetsNode(
        "7b",
        "Google Sheets: Log birthday milestone and status",
        1480,
        "Birthday_Milestones",
        '[["{subscriber_phone}","{first_name}","{loyalty_tier}","{offer_redeemed}","{today_date}"]]',
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4b", "output_2"),
      e("4", "5"),
      e("4b", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "7b", "output_1"),
    ],
    summary:
      "Fires daily to identify Attentive subscribers with birthdays today. Fetches loyalty profile and sends VIP-tier 30% off or standard 20% off birthday SMS. After 5 days, checks redemption and sends a last-chance reminder if unused, or logs the milestone to Google Sheets if redeemed. Drives birthday-driven repeat purchases with tier-differentiated rewards.",
  },

  // ────────────────────────────────────────────────────────────────
  // 77. Subscriber Anniversary SMS | Attentive | Pattern H
  // ────────────────────────────────────────────────────────────────
  subscriber_anniversary_sms: {
    nodes: [
      trigger("Attentive: Subscriber anniversary date reached", "schedule", {
        source: "attentive",
        event: "subscriber.anniversary",
        schedule:
          "Daily at 9 AM — checks for subscribers whose signup anniversary is today",
        howItStarts:
          "Runs daily to find Attentive subscribers who signed up exactly 1, 2, or 3+ years ago, triggering a celebratory reward SMS.",
        setup:
          "Connect Attentive subscriber data with signup date. Schedule daily at 9 AM to match anniversaries.",
        inputFields: [],
      }),
      n("2", "hitApi", "Attentive: Lookup subscriber tenure and spend", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "attributes.phone",
              customName: "subscriber_phone",
              type: "string",
            },
            {
              name: "attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "attributes.signup_date",
              customName: "signup_date",
              type: "string",
            },
            {
              name: "attributes.years_subscribed",
              customName: "years_subscribed",
              type: "number",
            },
            {
              name: "attributes.total_spent",
              customName: "total_spent",
              type: "number",
            },
          ],
        },
      }),
      n("3", "condition", "Check if 3+ year loyal subscriber", 520, {
        conditionDetails: {
          rules: [
            {
              field: "years_subscribed",
              operator: "greaterThanOrEqual",
              value: "3",
            },
          ],
        },
      }),
      smsNode(
        "4",
        "Attentive: Send premium anniversary SMS with $25 credit",
        760,
        "{subscriber_phone}",
        "{first_name}, it's your {years_subscribed}-year anniversary with us! 🎊 As a thank-you for your loyalty, here's a $25 store credit — no minimum: {credit_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "4b",
        "Attentive: Send anniversary SMS with 15% off",
        760,
        "{subscriber_phone}",
        "Happy anniversary, {first_name}! 🥳 You've been with us {years_subscribed} year(s). Celebrate with 15% off your next order — code ANNIV15: {store_link}. Reply STOP to opt out.",
        480,
      ),
      n("5", "delayAction", "Wait 7 days for reward usage", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("6", "condition", "Check if anniversary reward was used", 1240, {
        conditionDetails: {
          rules: [
            { field: "reward_redeemed", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "7",
        "Attentive: Send reward expiry reminder SMS",
        1480,
        "{subscriber_phone}",
        "Hey {first_name}, your anniversary reward expires in 3 days! Use your {reward_description} before it's gone: {store_link}. Reply STOP to opt out.",
      ),
      sheetsNode(
        "7b",
        "Google Sheets: Log anniversary milestone",
        1480,
        "Anniversary_Tracking",
        '[["{subscriber_phone}","{first_name}","{years_subscribed}","{reward_redeemed}","{today_date}"]]',
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4b", "output_2"),
      e("4", "5"),
      e("4b", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "7b", "output_1"),
    ],
    summary:
      "Daily schedule identifies Attentive subscribers on their signup anniversary. Looks up tenure and spend to determine reward tier — 3+ year loyalists get $25 store credit, others get 15% off. After 7 days, unredeemed rewards trigger a reminder SMS while redeemed ones are logged to Google Sheets for loyalty analytics.",
  },

  // ────────────────────────────────────────────────────────────────
  // 78. Loyalty Reward Reminder SMS | Attentive | Pattern H
  // ────────────────────────────────────────────────────────────────
  loyalty_reward_reminder_sms: {
    nodes: [
      trigger("Attentive: Loyalty reward becomes available", "webhook_app", {
        source: "attentive",
        event: "loyalty.reward_available",
        howItStarts:
          "Fires when a subscriber earns enough loyalty points to unlock a reward, or when an existing reward is approaching expiration within 14 days.",
        setup:
          "Connect Attentive to your loyalty platform (e.g., Smile.io, LoyaltyLion). Configure webhook for reward_available and reward_expiring events.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Attentive: Fetch loyalty balance and reward details",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "attributes.phone",
                customName: "subscriber_phone",
                type: "string",
              },
              {
                name: "attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "attributes.loyalty_points",
                customName: "loyalty_points",
                type: "number",
              },
              {
                name: "attributes.reward_name",
                customName: "reward_name",
                type: "string",
              },
              {
                name: "attributes.reward_expiry_date",
                customName: "reward_expiry_date",
                type: "string",
              },
              {
                name: "attributes.loyalty_tier",
                customName: "loyalty_tier",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "condition", "Check if VIP or Gold tier member", 520, {
        conditionDetails: {
          rules: [{ field: "loyalty_tier", operator: "in", value: "VIP,Gold" }],
        },
      }),
      smsNode(
        "4",
        "Attentive: Send VIP reward notification with bonus",
        760,
        "{subscriber_phone}",
        "{first_name}, your {reward_name} is ready! 🏆 As a {loyalty_tier} member, we've added a bonus surprise. Redeem now before {reward_expiry_date}: {redeem_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "4b",
        "Attentive: Send standard reward notification SMS",
        760,
        "{subscriber_phone}",
        "{first_name}, you've unlocked {reward_name}! 🎁 You have {loyalty_points} points. Redeem your reward before {reward_expiry_date}: {redeem_link}. Reply STOP to opt out.",
        480,
      ),
      n("5", "delayAction", "Wait 5 days for redemption", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "Check if reward was redeemed", 1240, {
        conditionDetails: {
          rules: [
            { field: "reward_redeemed", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "7",
        "Attentive: Send expiry warning SMS",
        1480,
        "{subscriber_phone}",
        "⏰ {first_name}, your {reward_name} expires on {reward_expiry_date}! Don't let it go to waste — redeem it here: {redeem_link}. Reply STOP to opt out.",
      ),
      sheetsNode(
        "7b",
        "Google Sheets: Log reward redemption status",
        1480,
        "Loyalty_Rewards",
        '[["{subscriber_phone}","{first_name}","{reward_name}","{loyalty_tier}","{reward_redeemed}","{today_date}"]]',
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4b", "output_2"),
      e("4", "5"),
      e("4b", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "7b", "output_1"),
    ],
    summary:
      "Triggered when a loyalty reward becomes available or nears expiration. Fetches the subscriber's loyalty profile and tier, then sends a VIP-enhanced notification with a bonus surprise or a standard reward alert. After 5 days, unredeemed rewards get an expiry warning SMS while redeemed outcomes are logged to Google Sheets for loyalty program analytics.",
  },

  // ────────────────────────────────────────────────────────────────
  // 79. Review Request SMS | Attentive | Pattern E
  // ────────────────────────────────────────────────────────────────
  review_request_sms: {
    nodes: [
      trigger("Attentive: Order delivered event received", "webhook_app", {
        source: "attentive",
        event: "order.delivered",
        howItStarts:
          "Fires when an order is marked as delivered by the shipping carrier, indicating the customer has received their purchase and is ready to review it.",
        setup:
          "Connect Attentive to your ecommerce platform (Shopify, BigCommerce). Configure delivery confirmation webhook from shipping carrier integration.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract order and product details",
        280,
        "You extract order details from delivery webhook payloads. Return JSON with product_name, product_category, order_id, customer_name, and review_prompt (a friendly 1-sentence prompt specific to the product).",
        "Delivery payload: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "review_prompt", as: "review_prompt", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Attentive: Lookup customer purchase history and VIP status",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "attributes.phone",
                customName: "subscriber_phone",
                type: "string",
              },
              {
                name: "attributes.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "attributes.is_vip",
                customName: "is_vip",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check if first-time buyer", 760, {
        conditionDetails: {
          rules: [{ field: "total_orders", operator: "equals", value: "1" }],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send first-buyer thank you + review ask",
        1000,
        "{subscriber_phone}",
        "Welcome to the family, {customer_name}! 💛 We hope you love your {product_name}. {review_prompt} Leave a quick review and get 10% off your next order: {review_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send returning customer review ask",
        1000,
        "{subscriber_phone}",
        "Hey {customer_name}, your {product_name} has arrived! {review_prompt} Share your thoughts — it takes 30 seconds: {review_link}. Reply STOP to opt out.",
        480,
      ),
      n("6", "delayAction", "Wait 7 days for review submission", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Check if review was submitted", 1480, {
        conditionDetails: {
          rules: [
            { field: "review_submitted", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send gentle review reminder SMS",
        1720,
        "{subscriber_phone}",
        "{customer_name}, we'd still love to hear about your {product_name}! Your review helps other shoppers — takes just 30 seconds: {review_link}. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Triggered on order delivery confirmation. AI extracts product details and generates a contextual review prompt. Checks purchase history to differentiate first-time buyers (who get a welcome message plus 10% incentive) from returning customers (simpler ask). After 7 days, non-reviewers receive a gentle reminder SMS to boost review collection rates.",
  },

  // ────────────────────────────────────────────────────────────────
  // 80. Post-Purchase Care Tips | Attentive | Pattern E
  // ────────────────────────────────────────────────────────────────
  post_purchase_care_tips: {
    nodes: [
      trigger("Attentive: Order placed event received", "webhook_app", {
        source: "attentive",
        event: "order.placed",
        howItStarts:
          "Fires when a customer completes a purchase through the connected ecommerce store, initiating a product care and education SMS sequence.",
        setup:
          "Connect Attentive to Shopify or your ecommerce platform. Enable the order.placed webhook event in Attentive integrations.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Generate product-specific care tips",
        280,
        "You generate product care tips based on purchase data. Return JSON with product_name, care_tip_1 (short, actionable tip), care_tip_2 (second tip), product_category, and care_guide_url (a relevant guide page path).",
        "Order data: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "care_tip_1", as: "care_tip_1", type: "string" },
          { field: "care_tip_2", as: "care_tip_2", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "care_guide_url", as: "care_guide_url", type: "string" },
        ],
      ),
      n("3", "hitApi", "Attentive: Lookup customer profile and tenure", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "attributes.phone",
              customName: "subscriber_phone",
              type: "string",
            },
            {
              name: "attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "attributes.total_orders",
              customName: "total_orders",
              type: "number",
            },
            {
              name: "attributes.is_first_purchase",
              customName: "is_first_purchase",
              type: "boolean",
            },
          ],
        },
      }),
      n("4", "condition", "Check if first purchase", 760, {
        conditionDetails: {
          rules: [
            { field: "is_first_purchase", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send first-buyer welcome + care tips SMS",
        1000,
        "{subscriber_phone}",
        "Welcome, {first_name}! 🎉 Thanks for your first order. Pro tip for your {product_name}: {care_tip_1}. Full care guide here: {care_guide_url}. Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send returning buyer care tips SMS",
        1000,
        "{subscriber_phone}",
        "Thanks for your order, {first_name}! Quick tip for your new {product_name}: {care_tip_1}. Also: {care_tip_2}. Care guide: {care_guide_url}. Reply STOP to opt out.",
        480,
      ),
      n("6", "delayAction", "Wait 10 days for product usage", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),
      n("7", "condition", "Check if product was delivered", 1480, {
        conditionDetails: {
          rules: [
            { field: "delivery_confirmed", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send follow-up cross-sell SMS",
        1720,
        "{subscriber_phone}",
        "{first_name}, how are you enjoying your {product_name}? Customers who love it also grab these: {cross_sell_link}. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggered on order placement. AI generates product-specific care tips and a care guide URL. First-time buyers receive a warm welcome with their first tip, while returning customers get both tips. After 10 days, once delivery is confirmed, a cross-sell SMS recommends complementary products, driving repeat purchase rates through education-led engagement.",
  },

  // ────────────────────────────────────────────────────────────────
  // 81. Winback SMS | Attentive | Pattern G
  // ────────────────────────────────────────────────────────────────
  winback_sms: {
    nodes: [
      trigger("Attentive: 60-day inactivity threshold crossed", "schedule", {
        source: "attentive",
        event: "subscriber.inactive_60d",
        schedule:
          "Daily at 10 AM — identifies subscribers with no engagement or purchase in the last 60 days",
        howItStarts:
          "Runs daily to find Attentive subscribers who have not opened an SMS, clicked a link, or made a purchase in 60 days, triggering a winback sequence.",
        setup:
          "Configure Attentive segment for 60-day inactivity based on last SMS click, site visit, or purchase event. Schedule daily at 10 AM.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Attentive: Fetch last activity and purchase history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "attributes.phone",
                customName: "subscriber_phone",
                type: "string",
              },
              {
                name: "attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "attributes.last_purchase_date",
                customName: "last_purchase_date",
                type: "string",
              },
              {
                name: "attributes.total_spent",
                customName: "total_spent",
                type: "number",
              },
              {
                name: "attributes.favorite_category",
                customName: "favorite_category",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Analyze inactivity reason and craft winback angle",
        520,
        "You analyze inactive customer profiles and suggest a winback approach. Return JSON with inactivity_reason (likely reason: seasonal, price-sensitive, product-gap, competitor), winback_hook (a compelling 1-sentence hook to re-engage), and recommended_offer (percentage or dollar amount).",
        "Customer: {first_name}, last purchase: {last_purchase_date}, total spent: ${total_spent}, favorite category: {favorite_category}",
        [
          {
            field: "inactivity_reason",
            as: "inactivity_reason",
            type: "string",
          },
          { field: "winback_hook", as: "winback_hook", type: "string" },
          {
            field: "recommended_offer",
            as: "recommended_offer",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Check if high-value customer (spent > $200)", 760, {
        conditionDetails: {
          rules: [
            { field: "total_spent", operator: "greaterThan", value: "200" },
          ],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send high-value winback SMS with exclusive offer",
        1000,
        "{subscriber_phone}",
        "We miss you, {first_name}! 💜 {winback_hook} Here's an exclusive {recommended_offer} off just for you — code COMEBACK: {store_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send standard winback SMS",
        1000,
        "{subscriber_phone}",
        "Hey {first_name}, it's been a while! {winback_hook} Come back with 15% off your next order — code MISSYOU15: {store_link}. Reply STOP to opt out.",
        480,
      ),
      n("6", "delayAction", "Wait 10 days for re-engagement", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),
      n("7", "condition", "Check if subscriber re-engaged", 1480, {
        conditionDetails: {
          rules: [{ field: "re_engaged", operator: "equals", value: "true" }],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send final winback or sunset warning SMS",
        1720,
        "{subscriber_phone}",
        "{first_name}, last chance! Your special offer expires tomorrow. If we don't hear from you, we'll reduce messages to keep your inbox clean: {store_link}. Reply STAY to keep hearing from us. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Daily schedule targets Attentive subscribers inactive for 60 days. AI analyzes the likely inactivity reason and crafts a personalized winback hook. High-value customers ($200+ LTV) get an exclusive AI-recommended offer, while others receive a standard 15% discount. After 10 days without re-engagement, a final sunset warning SMS gives them one last chance before message frequency is reduced.",
  },

  // ────────────────────────────────────────────────────────────────
  // 82. Quiet List Re-Engagement | Attentive | Pattern G
  // ────────────────────────────────────────────────────────────────
  quiet_list_reengagement: {
    nodes: [
      trigger(
        "Attentive: 90-day no-engagement segment identified",
        "schedule",
        {
          source: "attentive",
          event: "subscriber.no_engagement_90d",
          schedule:
            "Weekly on Monday at 9 AM — processes subscribers with zero SMS opens or clicks in 90 days",
          howItStarts:
            "Runs weekly to identify Attentive subscribers who have not clicked any SMS link, visited the site, or made a purchase in 90 days, triggering a re-engagement attempt before suppression.",
          setup:
            "Create Attentive segment for 90-day no-engagement (no clicks, no site visits, no purchases). Schedule weekly Monday at 9 AM.",
          inputFields: [],
        },
      ),
      n("2", "hitApi", "Attentive: Fetch subscriber engagement history", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "attributes.phone",
              customName: "subscriber_phone",
              type: "string",
            },
            {
              name: "attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "attributes.last_click_date",
              customName: "last_click_date",
              type: "string",
            },
            {
              name: "attributes.total_spent",
              customName: "total_spent",
              type: "number",
            },
            {
              name: "attributes.subscription_date",
              customName: "subscription_date",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI: Determine re-engagement strategy",
        520,
        "You analyze disengaged SMS subscribers and determine the best re-engagement strategy. Return JSON with engagement_score (0-100), best_angle (new-arrivals, price-drop, exclusive-access, or preference-update), and personalized_hook (1-sentence re-engagement message).",
        "Subscriber: {first_name}, last click: {last_click_date}, total spent: ${total_spent}, subscribed since: {subscription_date}",
        [
          { field: "engagement_score", as: "engagement_score", type: "number" },
          { field: "best_angle", as: "best_angle", type: "string" },
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Check if historically high-value (spent > $150)",
        760,
        {
          conditionDetails: {
            rules: [
              { field: "total_spent", operator: "greaterThan", value: "150" },
            ],
          },
        },
      ),
      smsNode(
        "5",
        "Attentive: Send high-value re-engagement SMS with incentive",
        1000,
        "{subscriber_phone}",
        "{first_name}, we have something special waiting for you! {personalized_hook} Enjoy 25% off as a thank-you for being a loyal customer — code RETURN25: {store_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send standard re-engagement SMS",
        1000,
        "{subscriber_phone}",
        "Hey {first_name}! {personalized_hook} Check out what's new: {store_link}. Not interested? Reply STOP and we'll stop messaging.",
        480,
      ),
      n("6", "delayAction", "Wait 14 days for re-engagement signal", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      n("7", "condition", "Check if subscriber re-engaged", 1480, {
        conditionDetails: {
          rules: [
            {
              field: "clicked_or_purchased",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send preference update or suppress SMS",
        1720,
        "{subscriber_phone}",
        "{first_name}, we want to send you only what you care about. Update your preferences here: {preference_link} — or we'll pause messages to respect your time. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Weekly schedule processes Attentive subscribers with zero engagement over 90 days. AI determines the best re-engagement angle and crafts a personalized hook. High-value subscribers get a 25% incentive, while others receive a lighter touch. After 14 days without a click or purchase, a final SMS offers preference updates or initiates suppression, maintaining list health and deliverability.",
  },

  // ────────────────────────────────────────────────────────────────
  // 83. Product Launch Waitlist | Attentive | Pattern I
  // ────────────────────────────────────────────────────────────────
  product_launch_waitlist: {
    nodes: [
      trigger(
        "Attentive: Waitlist signup received via SMS keyword",
        "webhook_app",
        {
          source: "attentive",
          event: "subscriber.waitlist_signup",
          howItStarts:
            "Fires when a subscriber texts a product-specific keyword (e.g., WAITLIST or the product name) to join the waitlist for an upcoming product launch.",
          setup:
            "Create Attentive keyword for the product waitlist (e.g., 'NEWDROP'). Connect webhook to fire on keyword match. Tag subscribers with the product waitlist segment.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract waitlist product and subscriber context",
        280,
        "You extract product waitlist details from signup events. Return JSON with product_name, launch_date, product_category, price_range, and excitement_message (a 1-sentence hype builder for the upcoming product).",
        "Waitlist signup payload: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "launch_date", as: "launch_date", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "price_range", as: "price_range", type: "string" },
          {
            field: "excitement_message",
            as: "excitement_message",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Attentive: Lookup subscriber profile and purchase history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "attributes.phone",
                customName: "subscriber_phone",
                type: "string",
              },
              {
                name: "attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "attributes.is_vip",
                customName: "is_vip",
                type: "boolean",
              },
              {
                name: "attributes.total_orders",
                customName: "total_orders",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check if VIP or high-frequency buyer", 760, {
        conditionDetails: {
          rules: [{ field: "is_vip", operator: "equals", value: "true" }],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send VIP early access confirmation SMS",
        1000,
        "{subscriber_phone}",
        "You're on the list, {first_name}! 🔥 {excitement_message} As a VIP, you'll get exclusive early access 24 hours before everyone else. Launching {launch_date} — we'll text you first! Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send standard waitlist confirmation SMS",
        1000,
        "{subscriber_phone}",
        "You're on the {product_name} waitlist, {first_name}! {excitement_message} We'll text you the moment it drops on {launch_date}. Stay tuned! Reply STOP to opt out.",
        480,
      ),
      n("6", "delayAction", "Wait 3 days before launch teaser", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check if subscriber engaged with confirmation",
        1480,
        {
          conditionDetails: {
            rules: [
              {
                field: "confirmation_clicked",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),
      smsNode(
        "8",
        "Attentive: Send launch countdown teaser SMS",
        1720,
        "{subscriber_phone}",
        "⏳ {first_name}, {product_name} drops in just a few days! {price_range}. Get your cart ready — we'll send your exclusive link the moment it's live: {teaser_link}. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggered when a subscriber joins a product waitlist via SMS keyword. AI extracts product details and generates hype messaging. VIP subscribers are promised 24-hour early access while standard waitlisters get a confirmation. After 3 days, engaged subscribers receive a countdown teaser SMS with pricing details to build anticipation and drive launch-day conversion.",
  },

  // ────────────────────────────────────────────────────────────────
  // 84. Keyword Signup to Welcome Flow | Attentive | Pattern A
  // ────────────────────────────────────────────────────────────────
  keyword_signup_welcome_flow: {
    nodes: [
      trigger("Attentive: SMS keyword opt-in received", "webhook_app", {
        source: "attentive",
        event: "subscriber.keyword_optin",
        howItStarts:
          "Fires when a new subscriber texts a keyword (e.g., JOIN, DEALS, or a branded keyword) to opt into SMS marketing via Attentive.",
        setup:
          "Configure Attentive keyword(s) for opt-in (e.g., 'JOIN' to short code). Ensure double opt-in compliance is enabled. Connect webhook for new subscriber events.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract signup context and keyword intent",
        280,
        "You analyze SMS keyword opt-in events and determine subscriber intent. Return JSON with keyword_used, signup_source (in-store, social, website, ad), inferred_interest (product category or content type based on keyword), and welcome_message (personalized 1-sentence welcome based on context).",
        "Keyword opt-in payload: {webhook_payload}",
        [
          { field: "keyword_used", as: "keyword_used", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "inferred_interest",
            as: "inferred_interest",
            type: "string",
          },
          { field: "welcome_message", as: "welcome_message", type: "string" },
        ],
      ),
      n("3", "hitApi", "Attentive: Check if existing customer in CRM", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "attributes.phone",
              customName: "subscriber_phone",
              type: "string",
            },
            {
              name: "attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "attributes.email",
              customName: "subscriber_email",
              type: "string",
            },
            {
              name: "attributes.has_previous_purchase",
              customName: "has_previous_purchase",
              type: "boolean",
            },
          ],
        },
      }),
      n("4", "condition", "Check if existing customer", 760, {
        conditionDetails: {
          rules: [
            {
              field: "has_previous_purchase",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send returning customer welcome SMS",
        1000,
        "{subscriber_phone}",
        "Welcome back, {first_name}! 🙌 {welcome_message} As a returning customer, here's 10% off your next order — code SMSBACK10: {store_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send new subscriber welcome SMS with first offer",
        1000,
        "{subscriber_phone}",
        "Hey there! {welcome_message} Welcome to the fam — enjoy 15% off your first order with code SMSNEW15: {store_link}. Reply STOP to opt out.",
        480,
      ),
      n("6", "delayAction", "Wait 24 hours for first engagement", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Check if subscriber clicked or purchased", 1480, {
        conditionDetails: {
          rules: [
            { field: "first_action_taken", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send follow-up education SMS",
        1720,
        "{subscriber_phone}",
        "Glad you're here! Here's what you can expect from us: exclusive drops, insider deals, and early access. Browse our top picks in {inferred_interest}: {category_link}. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Triggered when a new subscriber opts in via SMS keyword. AI analyzes the keyword to infer intent and signup source. Existing customers get a 10% welcome-back offer, while new subscribers receive 15% off their first order. After 24 hours, subscribers who haven't clicked yet get a follow-up education SMS showcasing the value of staying subscribed, tailored to their inferred interest.",
  },

  // ────────────────────────────────────────────────────────────────
  // 85. Geofence Store Visit Follow-Up | Attentive | Pattern A
  // ────────────────────────────────────────────────────────────────
  geofence_store_visit_followup: {
    nodes: [
      trigger("Attentive: Geofence store visit detected", "webhook_app", {
        source: "attentive",
        event: "subscriber.geofence_entered",
        howItStarts:
          "Fires when an Attentive subscriber enters a geofenced store location, detected via their mobile device location services and the Attentive SDK.",
        setup:
          "Configure geofence zones around physical store locations in Attentive. Enable location-based targeting. Ensure subscribers have location permissions enabled.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract store visit context and personalization",
        280,
        "You analyze geofence store visit events and generate personalization. Return JSON with store_name, store_city, visit_time (morning/afternoon/evening), suggested_offer (contextual to time of day and store), and greeting (1-sentence warm greeting).",
        "Geofence event payload: {webhook_payload}",
        [
          { field: "store_name", as: "store_name", type: "string" },
          { field: "store_city", as: "store_city", type: "string" },
          { field: "visit_time", as: "visit_time", type: "string" },
          { field: "suggested_offer", as: "suggested_offer", type: "string" },
          { field: "greeting", as: "greeting", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Attentive: Lookup subscriber purchase and visit history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "attributes.phone",
                customName: "subscriber_phone",
                type: "string",
              },
              {
                name: "attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "attributes.total_store_visits",
                customName: "total_store_visits",
                type: "number",
              },
              {
                name: "attributes.last_in_store_purchase",
                customName: "last_in_store_purchase",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check if repeat store visitor (3+ visits)", 760, {
        conditionDetails: {
          rules: [
            {
              field: "total_store_visits",
              operator: "greaterThanOrEqual",
              value: "3",
            },
          ],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send loyal visitor thank-you SMS",
        1000,
        "{subscriber_phone}",
        "{greeting} {first_name}! Thanks for visiting {store_name} again. {suggested_offer}. Show this text at checkout for your reward! Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send new visitor welcome SMS",
        1000,
        "{subscriber_phone}",
        "{greeting} {first_name}! Welcome to {store_name} in {store_city}. {suggested_offer}. Show this text to a team member for a special surprise! Reply STOP to opt out.",
        480,
      ),
      n("6", "delayAction", "Wait 48 hours after store visit", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "condition", "Check if in-store purchase was made", 1480, {
        conditionDetails: {
          rules: [
            {
              field: "in_store_purchase_made",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send post-visit follow-up SMS",
        1720,
        "{subscriber_phone}",
        "Thanks for visiting {store_name}, {first_name}! Didn't find what you were looking for? Browse our full collection online with free shipping: {online_store_link}. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Triggered when a subscriber enters a geofenced store location. AI contextualizes the visit with time-of-day and store details. Repeat visitors (3+) get a loyalty reward offer, while new visitors receive a special surprise incentive. After 48 hours, subscribers who did not make an in-store purchase get a follow-up SMS directing them to the online store with free shipping to capture the conversion.",
  },

  // ────────────────────────────────────────────────────────────────
  // 86. Order Shipping SMS Updates | Attentive | Pattern E
  // ────────────────────────────────────────────────────────────────
  order_shipping_sms_updates: {
    nodes: [
      trigger("Attentive: Order shipped event received", "webhook_app", {
        source: "attentive",
        event: "order.shipped",
        howItStarts:
          "Fires when an order's shipping status changes to 'shipped' from the connected ecommerce platform, providing the tracking number and carrier details.",
        setup:
          "Connect Attentive to Shopify or your ecommerce platform with shipping carrier integrations (UPS, FedEx, USPS). Enable order.shipped webhook events.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract shipping and order details",
        280,
        "You extract shipping details from order shipped events. Return JSON with order_id, product_summary (brief list of items), carrier_name, tracking_number, estimated_delivery, and delivery_message (friendly 1-sentence delivery expectation).",
        "Order shipped payload: {webhook_payload}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "product_summary", as: "product_summary", type: "string" },
          { field: "carrier_name", as: "carrier_name", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          {
            field: "estimated_delivery",
            as: "estimated_delivery",
            type: "string",
          },
          { field: "delivery_message", as: "delivery_message", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Attentive: Lookup customer profile and VIP status",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "attributes.phone",
                customName: "subscriber_phone",
                type: "string",
              },
              {
                name: "attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "attributes.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "attributes.is_first_purchase",
                customName: "is_first_purchase",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check if first order for this customer", 760, {
        conditionDetails: {
          rules: [
            { field: "is_first_purchase", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send first-order shipping excitement SMS",
        1000,
        "{subscriber_phone}",
        "Your first order is on its way, {first_name}! 📦 {product_summary} is shipping via {carrier_name}. {delivery_message} Track it here: {tracking_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send standard shipping notification SMS",
        1000,
        "{subscriber_phone}",
        "{first_name}, your order #{order_id} has shipped! {delivery_message} Track via {carrier_name}: {tracking_link}. Reply STOP to opt out.",
        480,
      ),
      n("6", "delayAction", "Wait 5 days for delivery to complete", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Check if order was delivered", 1480, {
        conditionDetails: {
          rules: [
            { field: "delivery_confirmed", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send delivery confirmation and review prompt",
        1720,
        "{subscriber_phone}",
        "Your {product_summary} has been delivered, {first_name}! 🎉 We hope you love it. Quick question — how was your experience? Leave a review: {review_link}. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggered when an order ships. AI extracts shipping details including carrier, tracking number, and estimated delivery. First-time buyers get an excited shipping notification, while returning customers get a concise update. After 5 days, once delivery is confirmed, a follow-up SMS prompts a review, turning the transactional shipping touchpoint into a post-purchase engagement opportunity.",
  },

  // ────────────────────────────────────────────────────────────────
  // 87. Payment Failure SMS Recovery | Attentive | Pattern F
  // ────────────────────────────────────────────────────────────────
  payment_failure_sms_recovery: {
    nodes: [
      trigger("Attentive: Payment failure event received", "webhook_app", {
        source: "attentive",
        event: "subscription.payment_failed",
        howItStarts:
          "Fires when a recurring subscription payment fails — declined card, insufficient funds, or expired payment method — requiring the customer to update their billing information.",
        setup:
          "Connect Attentive to your subscription billing platform (Stripe, ReCharge, Bold). Enable webhook for payment_failed events. Map subscriber phone to billing account.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract payment failure details",
        280,
        "You analyze payment failure events and extract details. Return JSON with failure_reason (card_declined, insufficient_funds, expired_card, processing_error), subscription_name, amount_due, next_retry_date, and urgency_level (low, medium, high based on retry attempts).",
        "Payment failure payload: {webhook_payload}",
        [
          { field: "failure_reason", as: "failure_reason", type: "string" },
          {
            field: "subscription_name",
            as: "subscription_name",
            type: "string",
          },
          { field: "amount_due", as: "amount_due", type: "string" },
          { field: "next_retry_date", as: "next_retry_date", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),
      n("3", "hitApi", "Stripe: Check payment status and retry count", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/subscriptions/{subscription_id}",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          body: [],
          variables: [
            {
              name: "status",
              customName: "subscription_status",
              type: "string",
            },
            {
              name: "latest_invoice.attempt_count",
              customName: "retry_count",
              type: "number",
            },
            {
              name: "customer",
              customName: "stripe_customer_id",
              type: "string",
            },
            {
              name: "metadata.phone",
              customName: "subscriber_phone",
              type: "string",
            },
            {
              name: "metadata.first_name",
              customName: "first_name",
              type: "string",
            },
          ],
        },
      }),
      n(
        "4",
        "condition",
        "Check if payment at risk of cancellation (3+ retries)",
        760,
        {
          conditionDetails: {
            rules: [
              {
                field: "retry_count",
                operator: "greaterThanOrEqual",
                value: "3",
              },
            ],
          },
        },
      ),
      smsNode(
        "5",
        "Attentive: Send urgent payment recovery SMS",
        1000,
        "{subscriber_phone}",
        "⚠️ {first_name}, your {subscription_name} subscription is about to be cancelled! Your payment of {amount_due} failed. Please update your card now to avoid losing access: {payment_update_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send friendly payment update request SMS",
        1000,
        "{subscriber_phone}",
        "Heads up, {first_name} — your {amount_due} payment for {subscription_name} didn't go through ({failure_reason}). Update your payment info here: {payment_update_link}. We'll retry on {next_retry_date}. Reply STOP to opt out.",
        480,
      ),
      n("6", "delayAction", "Wait 3 days for payment resolution", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Check if payment was resolved", 1480, {
        conditionDetails: {
          rules: [
            { field: "payment_resolved", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send final payment warning SMS",
        1720,
        "{subscriber_phone}",
        "Final notice, {first_name}: your {subscription_name} will be cancelled tomorrow if payment isn't updated. Don't lose your subscription benefits — update now: {payment_update_link}. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Triggered on subscription payment failure. AI classifies the failure reason and urgency level. Stripe confirms retry count — subscribers at 3+ failed attempts get an urgent cancellation warning, while first-time failures receive a friendly update request. After 3 days, unresolved payments trigger a final warning SMS before cancellation, maximizing involuntary churn recovery.",
  },

  // ────────────────────────────────────────────────────────────────
  // 88. Replenishment Reminder SMS | Attentive | Pattern F
  // ────────────────────────────────────────────────────────────────
  replenishment_reminder_sms: {
    nodes: [
      trigger("Attentive: Predicted reorder date approaching", "schedule", {
        source: "attentive",
        event: "subscriber.reorder_predicted",
        schedule:
          "Daily at 11 AM — checks for subscribers whose predicted replenishment date is 5 days away",
        howItStarts:
          "Runs daily to identify Attentive subscribers who purchased consumable or replenishable products and are approaching their predicted reorder date based on average consumption cycle.",
        setup:
          "Configure product replenishment cycles in your ecommerce platform (e.g., 30 days for skincare, 60 days for supplements). Connect Attentive with purchase history data to compute predicted reorder dates.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Analyze purchase pattern and generate reorder context",
        280,
        "You analyze customer replenishment patterns and generate reorder messaging. Return JSON with product_name, days_since_purchase, estimated_supply_remaining (percentage), reorder_urgency (low, medium, high), and reorder_message (1-sentence personalized prompt to reorder).",
        "Customer purchase data: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          {
            field: "days_since_purchase",
            as: "days_since_purchase",
            type: "number",
          },
          {
            field: "estimated_supply_remaining",
            as: "estimated_supply_remaining",
            type: "string",
          },
          { field: "reorder_urgency", as: "reorder_urgency", type: "string" },
          { field: "reorder_message", as: "reorder_message", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Attentive: Fetch subscriber profile and subscription status",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/subscribers/{subscriber_id}/attributes",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "attributes.phone",
                customName: "subscriber_phone",
                type: "string",
              },
              {
                name: "attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "attributes.has_subscription",
                customName: "has_subscription",
                type: "boolean",
              },
              {
                name: "attributes.total_reorders",
                customName: "total_reorders",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check if already on auto-subscription", 760, {
        conditionDetails: {
          rules: [
            { field: "has_subscription", operator: "equals", value: "false" },
          ],
        },
      }),
      smsNode(
        "5",
        "Attentive: Send reorder reminder with subscribe-and-save offer",
        1000,
        "{subscriber_phone}",
        "Running low on {product_name}, {first_name}? {reorder_message} Reorder now and save 15% when you subscribe: {subscribe_save_link}. Reply STOP to opt out.",
      ),
      smsNode(
        "5b",
        "Attentive: Send subscription renewal heads-up SMS",
        1000,
        "{subscriber_phone}",
        "Heads up, {first_name}! Your {product_name} subscription refill ships soon. Need to adjust quantity or skip? Manage here: {subscription_manage_link}. Reply STOP to opt out.",
        480,
      ),
      n("6", "delayAction", "Wait 5 days for reorder action", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Check if reorder was placed", 1480, {
        conditionDetails: {
          rules: [
            { field: "reorder_placed", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "8",
        "Attentive: Send last-chance replenishment reminder SMS",
        1720,
        "{subscriber_phone}",
        "{first_name}, you're probably almost out of {product_name}! Don't run dry — reorder in 1 tap and get free shipping: {reorder_link}. Reply STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Daily schedule identifies subscribers approaching their predicted reorder date for consumable products. AI estimates remaining supply and generates urgency-appropriate messaging. Non-subscribers get a reorder reminder with a 15% subscribe-and-save offer, while existing subscribers get a refill management heads-up. After 5 days, non-reorderers receive a last-chance SMS with free shipping to capture the replenishment conversion.",
  },
};
